import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { canAutoReload, isChunkLoadError, reloadOnceForChunkError } from "@/lib/chunkReload";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** A route chunk went missing under a deploy: the page is reloading itself, so no error screen. */
  reloading: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
  }

  // Decided in the render phase so the error screen never flashes before the reload.
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, reloading: isChunkLoadError(error) && canAutoReload() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.state.reloading) {
      // The page is going away; nothing to log or report.
      if (reloadOnceForChunkError(error)) return;
      // The reload couldn't be recorded (storage blocked): show the screen rather than loop.
      this.setState({ reloading: false });
    }
    console.error("ErrorBoundary caught:", error, errorInfo);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
      // A chunk error that outlived the reload window is a real outage, not a stale tab.
      tags: isChunkLoadError(error) ? { chunk_reload: "exhausted" } : undefined,
    });
  }

  handleReset = () => {
    // React.lazy remembers a failed import, so only a reload recovers a chunk error.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null, reloading: false });
  };

  render() {
    if (this.state.reloading) {
      return <div className="min-h-screen bg-[#faf9f5]" aria-busy="true" />;
    }

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="flex flex-col items-center gap-4 max-w-md text-center">
            <h2 className="text-xl font-semibold text-stone-800">
              Something went wrong
            </h2>
            <p className="text-stone-500">
              An unexpected error occurred. Please try again.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={this.handleReset}>
                Try again
              </Button>
              <Button onClick={() => window.location.reload()}>
                Reload page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
